



















































































/* eslint-disable no-await-in-loop */
import { Component, Vue } from 'vue-property-decorator'
import { container } from 'inversify-props'
import type { Loja, NotaDaVenda, ResumoDaVenda } from '@/models'
import type { PedidoDeVenda } from '@/models/venda/PedidoDeVenda'
import type { PedidoServiceAdapter } from '@/usecases/venda/adapter/PedidoServiceAdapter'
import { FindLojaUseCase, FindVendaUseCase } from '@/usecases'
import { obterBytesNotaFiscal, obterPdfDaNotaAutorizada } from '@/shareds/venda-shareds'
import { mergeAndOpenPdfs } from '@/shareds/pdf-utils'
import mapErrosSefaz from '@/shareds/fiscal/tabelas/mapErrosSefaz'
import { getRequestErrorMessage } from '@/helpers/axios-helpers'
import UserLoginStore from '@/store/vuex/authentication/UserLoginStore'
import AlertModule from '@/store/vuex/aplicacao/AlertModule'

type StatusDoFaturamento = 'emitido' | 'reimpresso' | 'erro' | 'bloqueado'

interface ResultadoDoFaturamento {
	venda: ResumoDaVenda
	status: StatusDoFaturamento
	mensagem: string
}

const SITUACOES_FATURAVEIS = ['Pendente', 'Em Separação']

@Component
export default class DialogoDeFaturamentoEmMassa extends Vue {
	mostra = false
	validando = false
	processando = false
	imprimindo = false
	total = 0
	indiceAtual = 0
	identificadorAtual = ''
	resultados: ResultadoDoFaturamento[] = []
	pdfs: Uint8Array[] = []

	findVendaUseCase = new FindVendaUseCase()
	findLojaUseCase = new FindLojaUseCase()
	pedidoService = container.get<PedidoServiceAdapter>('PedidoServiceAdapter')
	lojas: Record<string, Loja> = {}

	iconeDoStatus: Record<StatusDoFaturamento, { icone: string; cor: string }> = {
		emitido: { icone: 'mdi-check-circle', cor: 'success' },
		reimpresso: { icone: 'mdi-printer-check', cor: 'info' },
		erro: { icone: 'mdi-alert-circle', cor: 'error' },
		bloqueado: { icone: 'mdi-cancel', cor: 'error' },
	}

	get executando() {
		return this.validando || this.processando || this.imprimindo
	}

	get percentual() {
		return this.total ? Math.round(((this.indiceAtual - 1) / this.total) * 100) : 0
	}

	get podeLiberarVendaNaoContribuinte() {
		return UserLoginStore.permiteRegraDeNegocio('pode-liberar-venda-fora-do-estado')
	}

	quantidadePorStatus(status: StatusDoFaturamento) {
		return this.resultados.filter(resultado => resultado.status === status).length
	}

	async iniciar(vendas: ResumoDaVenda[]) {
		if (this.executando || !vendas.length) return

		this.resultados = []
		this.pdfs = []
		this.lojas = {}
		this.total = 0
		this.indiceAtual = 0
		this.identificadorAtual = ''
		this.mostra = true

		const aProcessar: ResumoDaVenda[] = []
		const bloqueios: ResultadoDoFaturamento[] = []
		try {
			this.validando = true
			for (const venda of vendas) {
				const motivo = await this.motivoDeBloqueio(venda)
				motivo ? bloqueios.push(this.bloqueio(venda, motivo)) : aProcessar.push(venda)
			}
		} catch (error) {
			AlertModule.setError(error)
			this.mostra = false
			return
		} finally {
			this.validando = false
		}

		this.processando = true
		this.total = aProcessar.length
		for (const [indice, venda] of aProcessar.entries()) {
			this.indiceAtual = indice + 1
			this.identificadorAtual = venda.identificador || ''
			this.resultados.push(await this.processar(venda))
		}
		this.processando = false

		this.resultados.push(...bloqueios)

		if (this.pdfs.length) await this.imprimir()
		this.$emit('concluido')
	}

	async motivoDeBloqueio(venda: ResumoDaVenda): Promise<string | null> {
		const ultimaNota = this.ultimaNota(venda)
		const cstat = ultimaNota?.cstat || null

		if (cstat && cstat !== '100') {
			return `Último status da nota: ${this.descricaoDoStatus(cstat)}. ` +
				'Só é possível faturar/imprimir em massa pedidos sem nota ou com nota autorizada (100).'
		}

		if (cstat === '100') return null

		if (venda.situacao === 'Cancelada' || !SITUACOES_FATURAVEIS.includes(venda.situacaoPedido || '')) {
			return `Pedido na situação "${venda.situacaoPedido || venda.situacao}" não pode ser faturado.`
		}

		const loja = await this.buscarLoja(venda)
		if (loja.configuracaoDaLoja?.exigirVerificacaoDeProdutos) {
			return `A loja ${loja.nome} exige verificação de produtos para faturamento. Fature este pedido individualmente.`
		}

		return null
	}

	async processar(venda: ResumoDaVenda): Promise<ResultadoDoFaturamento> {
		const ultimaNota = this.ultimaNota(venda)
		if (ultimaNota && ultimaNota.cstat === '100') return this.reimprimir(venda, ultimaNota)
		return this.emitir(venda)
	}

	async reimprimir(venda: ResumoDaVenda, nota: NotaDaVenda): Promise<ResultadoDoFaturamento> {
		try {
			const pdf = await obterPdfDaNotaAutorizada(venda.id, nota.modelo === 'NF-e')
			this.pdfs.push(new Uint8Array(pdf))
		} catch (error) {
			return this.erro(venda, `Não foi possível reimprimir a nota ${nota.nnf}/${nota.serie}: ${this.mensagemDoErro(error)}`)
		}

		if (venda.pedido && SITUACOES_FATURAVEIS.includes(venda.situacaoPedido || '')) {
			try {
				await this.marcarComoFaturado(venda.pedido)
			} catch (error) {
				return this.erro(venda,
					`Nota ${nota.nnf}/${nota.serie} reimpressa, mas não foi possível atualizar a situação do pedido: ${this.mensagemDoErro(error)}`)
			}
		}

		return { venda, status: 'reimpresso', mensagem: `Nota ${nota.nnf}/${nota.serie} já autorizada, reimpressa.` }
	}

	async emitir(venda: ResumoDaVenda): Promise<ResultadoDoFaturamento> {
		let pedido: PedidoDeVenda | null
		try {
			const vendaComReferencias = await this.findVendaUseCase.getVendaComReferencias(venda.id)
			pedido = vendaComReferencias.pedido

			if (!pedido) return this.erro(venda, 'Venda sem pedido vinculado.')
			if (!vendaComReferencias.itens.some(item => !item.motivoCancelamento))
				return this.erro(venda, 'Pedido sem itens ativos para faturar.')
			if (vendaComReferencias.notaDeTransferenciaComplementarPendente)
				return this.erro(venda, 'Necessário receber a nota de transferência do estoque complementar antes de faturar.')

			const loja = await this.buscarLoja(venda)
			if (loja.configuracaoDaLoja?.exigirAutorizacaoParaVendaForaDoEstado && !this.podeLiberarVendaNaoContribuinte) {
				const vendaCompleta = await this.findVendaUseCase.get(venda.id)
				if (vendaCompleta.cliente?.inscricaoEstadual === 'ISENTO')
					return this.erro(venda,
						'Cliente não contribuinte (ISENTO) exige autorização para venda fora do estado. Fature este pedido individualmente.')
			}
		} catch (error) {
			return this.erro(venda, this.mensagemDoErro(error))
		}

		try {
			const pdf = await obterBytesNotaFiscal(venda.id, true, false)
			this.pdfs.push(new Uint8Array(pdf))
		} catch (error) {
			return this.erro(venda, await this.criticaDaEmissao(venda, error))
		}

		try {
			await this.marcarComoFaturado(pedido)
		} catch (error) {
			return this.erro(venda,
				`Nota autorizada, mas não foi possível atualizar a situação do pedido: ${this.mensagemDoErro(error)}`)
		}

		return { venda, status: 'emitido', mensagem: 'Nota autorizada e pedido faturado.' }
	}

	async marcarComoFaturado(pedido: PedidoDeVenda) {
		const pedidoAEnviar: PedidoDeVenda = JSON.parse(JSON.stringify(pedido))
		pedidoAEnviar.situacao = 'Faturado'
		await this.pedidoService.atualizarSituacao(pedidoAEnviar)
	}

	async criticaDaEmissao(venda: ResumoDaVenda, error: unknown): Promise<string> {
		const mensagem = this.mensagemDoErro(error)
		try {
			const vendaAtualizada = await this.findVendaUseCase.get(venda.id)
			const nota = this.ultimaNota(vendaAtualizada as any)
			if (nota && nota.cstat && !['0', '1', '100'].includes(nota.cstat)) {
				return `Rejeição SEFAZ ${this.descricaoDoStatus(nota.cstat)}` +
					(nota.nnf ? ` (nota ${nota.nnf}/${nota.serie})` : '') +
					(mensagem ? `. ${mensagem}` : '')
			}
		} catch(e) {
			AlertModule.setError(e)
		}
		return mensagem || 'Erro desconhecido na emissão da nota.'
	}

	async imprimir() {
		if (!this.pdfs.length) return
		try {
			this.imprimindo = true
			await mergeAndOpenPdfs(this.pdfs)
		} catch (error) {
			AlertModule.setError('Não foi possível abrir a impressão das notas. Use "Imprimir novamente".')
		} finally {
			this.imprimindo = false
		}
	}

	fechar() {
		this.mostra = false
	}

	async buscarLoja(venda: ResumoDaVenda): Promise<Loja> {
		const lojaId = venda.pontoDeVenda.loja.id
		if (!this.lojas[lojaId]) this.lojas[lojaId] = await this.findLojaUseCase.findLojaById(lojaId)
		return this.lojas[lojaId]
	}

	ultimaNota(venda: ResumoDaVenda): NotaDaVenda | null {
		const notas = venda.notas || []
		return notas.length ? notas[notas.length - 1] : null
	}

	descricaoDoStatus(cstat: string) {
		return mapErrosSefaz[cstat] ? `${cstat} - ${mapErrosSefaz[cstat]}` : cstat
	}

	mensagemDoErro(error: any): string {
		if (error?.response) return getRequestErrorMessage(null, error)
		if (typeof error === 'string') return error
		return error?.message || ''
	}

	bloqueio(venda: ResumoDaVenda, mensagem: string): ResultadoDoFaturamento {
		return { venda, status: 'bloqueado', mensagem }
	}

	erro(venda: ResumoDaVenda, mensagem: string): ResultadoDoFaturamento {
		return { venda, status: 'erro', mensagem }
	}
}
